if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId || this.id.replace('MediaGallery-', '');
        this.activeIndex = 0;
        this.modalIndex = 0;
        this.rafId = null;
        this.resizeRafId = null;
        this.slideShowTimer = null;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.isLightboxOpen = false;
        this.lastFocusedElement = null;
        this.galleryBound = false;
        this.lightboxBound = false;
        this.hasUserInteracted = false;

        this.onResize = this.onResize.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onFullscreenChange = this.onFullscreenChange.bind(this);
        this.onGalleryClick = this.onGalleryClick.bind(this);
        this.onLightboxClick = this.onLightboxClick.bind(this);
        this.onLightboxDoubleClick = this.onLightboxDoubleClick.bind(this);
        this.markUserInteracted = this.markUserInteracted.bind(this);
      }

      connectedCallback() {
        this.cache();

        if (!this.list || !this.items.length) return;

        this.bindGallery();
        this.updateGalleryState(0, false);
        this.setModalIndex(0, false);
        this.pauseAllVideos(this);
        requestAnimationFrame(() => {
  this.classList.add('is-ready');
});
      }

      disconnectedCallback() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);

        this.stopSlideshow();
        this.pauseAllVideos(this);
        this.pauseAllVideos(this.lightbox);

        window.removeEventListener('resize', this.onResize);

        if (this.galleryBound) {
          this.removeEventListener('click', this.onGalleryClick);
        }

        if (this.lightboxBound) {
          document.removeEventListener('keydown', this.onKeydown);
          document.removeEventListener('fullscreenchange', this.onFullscreenChange);

          if (this.lightbox) {
            this.lightbox.removeEventListener('click', this.onLightboxClick);
            this.lightbox.removeEventListener('dblclick', this.onLightboxDoubleClick);
          }
        }
      }

      cache() {
        this.list = this.querySelector('.cb-pdp-media-list');
        this.items = Array.from(this.querySelectorAll('.cb-pdp-media-item'));
        this.dots = Array.from(this.querySelectorAll('.cb-pdp-gallery__dot'));

        this.lightbox = document.getElementById(`CBProductLightbox-${this.sectionId}`);

        if (!this.lightbox) return;

        this.lightboxDialog = this.lightbox.querySelector('.cb-pdp-lightbox__dialog');
        this.lightboxViewport = this.lightbox.querySelector('[data-cb-lightbox-viewport]');
        this.lightboxTrack = this.lightbox.querySelector('[data-cb-lightbox-track]');
        this.modalSlides = Array.from(this.lightbox.querySelectorAll('[data-cb-lightbox-slide]'));
        this.modalCurrent = this.lightbox.querySelector('[data-cb-lightbox-current]');
        this.modalTotal = this.lightbox.querySelector('[data-cb-lightbox-total]');
        this.zoomButton = this.lightbox.querySelector('[data-cb-lightbox-zoom]');
        this.shareButton = this.lightbox.querySelector('[data-cb-lightbox-share]');
        this.playButton = this.lightbox.querySelector('[data-cb-lightbox-play]');
        this.fullscreenButton = this.lightbox.querySelector('[data-cb-lightbox-fullscreen]');
        this.thumbsToggle = this.lightbox.querySelector('[data-cb-lightbox-thumbs-toggle]');
        this.thumbsPanel = this.lightbox.querySelector('[data-cb-lightbox-thumbs]');
        this.modalThumbs = Array.from(this.lightbox.querySelectorAll('[data-cb-lightbox-thumb]'));
      }

      isTabletOrMobile() {
        return window.innerWidth < 990;
      }

      isMobileLightboxAllowed() {
        return window.innerWidth < 750;
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      saveDataEnabled() {
        return Boolean(navigator.connection && navigator.connection.saveData);
      }

      getScrollBehavior() {
        return this.prefersReducedMotion() ? 'auto' : 'smooth';
      }

      markUserInteracted() {
        this.hasUserInteracted = true;
      }

      bindGallery() {
        if (this.galleryBound) return;

        this.galleryBound = true;

        this.addEventListener('click', this.onGalleryClick);

        if (this.list) {
          this.list.addEventListener('scroll', this.onScroll, { passive: true });
          this.list.addEventListener('touchstart', this.markUserInteracted, { passive: true });
          this.list.addEventListener('pointerdown', this.markUserInteracted, { passive: true });
          this.list.addEventListener('wheel', this.markUserInteracted, { passive: true });
        }

        window.addEventListener('resize', this.onResize, { passive: true });
      }

      bindLightbox() {
        if (!this.lightbox || this.lightboxBound) return;

        this.lightboxBound = true;

        this.lightbox.addEventListener('click', this.onLightboxClick);
        this.lightbox.addEventListener('dblclick', this.onLightboxDoubleClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('fullscreenchange', this.onFullscreenChange);

        if (this.lightboxViewport) {
          this.lightboxViewport.addEventListener(
            'touchstart',
            (event) => {
              this.touchStartX = event.changedTouches[0].clientX;
            },
            { passive: true }
          );

          this.lightboxViewport.addEventListener(
            'touchend',
            (event) => {
              this.touchEndX = event.changedTouches[0].clientX;
              this.handleSwipe();
            },
            { passive: true }
          );
        }
      }

      onGalleryClick(event) {
        const prevButton = event.target.closest('.cb-pdp-gallery__arrow--prev');
        const nextButton = event.target.closest('.cb-pdp-gallery__arrow--next');
        const dot = event.target.closest('.cb-pdp-gallery__dot');
        const openButton = event.target.closest('[data-cb-lightbox-open]');

        if (prevButton) {
          event.preventDefault();
          this.markUserInteracted();
          this.goToIndex(this.activeIndex - 1);
          return;
        }

        if (nextButton) {
          event.preventDefault();
          this.markUserInteracted();
          this.goToIndex(this.activeIndex + 1);
          return;
        }

        if (dot) {
          event.preventDefault();
          this.markUserInteracted();

          this.cache();
          const dotIndex = this.dots.indexOf(dot);

          if (dotIndex >= 0) {
            this.goToIndex(dotIndex);
          }

          return;
        }

        if (openButton) {
          if (!this.isMobileLightboxAllowed()) return;

          event.preventDefault();
          this.markUserInteracted();
          this.cache();
          this.bindLightbox();

          const mediaId = openButton.dataset.modalMediaId;
          const activeItem = openButton.closest('.cb-pdp-media-item');
          const activeIndex = activeItem ? this.items.indexOf(activeItem) : this.activeIndex;
          const modalIndex = this.modalSlides.findIndex((slide) => slide.dataset.mediaId === mediaId);

          if (activeIndex >= 0) {
            this.activeIndex = activeIndex;
            this.updateGalleryState(activeIndex, false);
          }

          this.openLightbox(modalIndex >= 0 ? modalIndex : activeIndex);
        }
      }

      onLightboxClick(event) {
        const closeButton = event.target.closest('[data-cb-lightbox-close]');
        const prevButton = event.target.closest('[data-cb-lightbox-prev]');
        const nextButton = event.target.closest('[data-cb-lightbox-next]');
        const thumb = event.target.closest('[data-cb-lightbox-thumb]');
        const zoomButton = event.target.closest('[data-cb-lightbox-zoom]');
        const shareButton = event.target.closest('[data-cb-lightbox-share]');
        const playButton = event.target.closest('[data-cb-lightbox-play]');
        const fullscreenButton = event.target.closest('[data-cb-lightbox-fullscreen]');
        const thumbsToggle = event.target.closest('[data-cb-lightbox-thumbs-toggle]');

        if (closeButton) {
          event.preventDefault();
          this.closeLightbox();
          return;
        }

        if (prevButton) {
          event.preventDefault();
          event.stopPropagation();
          this.setModalIndex(this.modalIndex - 1);
          return;
        }

        if (nextButton) {
          event.preventDefault();
          event.stopPropagation();
          this.setModalIndex(this.modalIndex + 1);
          return;
        }

        if (thumb) {
          event.preventDefault();
          this.setModalIndex(Number(thumb.dataset.index));
          return;
        }

        if (zoomButton) {
          event.preventDefault();
          this.toggleZoom();
          return;
        }

        if (shareButton) {
          event.preventDefault();
          this.shareProduct();
          return;
        }

        if (playButton) {
          event.preventDefault();
          this.toggleSlideshow();
          return;
        }

        if (fullscreenButton) {
          event.preventDefault();
          this.toggleFullscreen();
          return;
        }

        if (thumbsToggle && this.thumbsPanel) {
          event.preventDefault();

          const willOpen = this.thumbsPanel.hidden;

          this.thumbsPanel.hidden = !willOpen;
          this.lightbox.classList.toggle('is-thumbs-open', willOpen);
          thumbsToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        }
      }

      onLightboxDoubleClick(event) {
        if (!event.target.closest('[data-cb-lightbox-zoom-surface]')) return;
        this.toggleZoom();
      }

      openLightbox(index) {
        if (!this.lightbox || !this.modalSlides.length) return;

        this.bindLightbox();

        this.lastFocusedElement = document.activeElement;
        this.isLightboxOpen = true;

        this.lightbox.hidden = false;
        this.lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cb-pdp-lightbox-open');
        document.documentElement.classList.add('cb-pdp-lightbox-open');

        this.pauseAllVideos(this);
        this.setModalIndex(index, true);

        requestAnimationFrame(() => {
          this.lightbox.classList.add('is-open');

          if (this.lightboxDialog) {
            this.lightboxDialog.focus();
          }
        });
      }

      closeLightbox() {
        if (!this.lightbox || !this.isLightboxOpen) return;

        this.isLightboxOpen = false;
        this.stopSlideshow();
        this.resetZoom();
        this.pauseAllVideos(this.lightbox);

        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }

        this.lightbox.classList.remove('is-open');
        this.lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cb-pdp-lightbox-open');
        document.documentElement.classList.remove('cb-pdp-lightbox-open');

        window.setTimeout(() => {
          if (!this.isLightboxOpen) {
            this.lightbox.hidden = true;
          }
        }, 240);

        this.syncInlineVideos();

        if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
          this.lastFocusedElement.focus();
        }
      }

      onKeydown(event) {
        if (!this.isLightboxOpen) return;

        if (event.key === 'Escape') {
          this.closeLightbox();
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.setModalIndex(this.modalIndex + 1);
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.setModalIndex(this.modalIndex - 1);
          return;
        }

        if (event.key.toLowerCase() === 'z') {
          event.preventDefault();
          this.toggleZoom();
        }
      }

      handleSwipe() {
        const difference = this.touchStartX - this.touchEndX;

        if (Math.abs(difference) < 45) return;

        if (difference > 0) {
          this.setModalIndex(this.modalIndex + 1);
        } else {
          this.setModalIndex(this.modalIndex - 1);
        }
      }

      setModalIndex(index, shouldPlay = true) {
        if (!this.lightboxTrack || !this.modalSlides.length) return;

        const total = this.modalSlides.length;
        const nextIndex = (index + total) % total;

        this.modalIndex = nextIndex;
        this.resetZoom();

        this.lightboxTrack.style.transform = `translate3d(-${nextIndex * 100}%, 0, 0)`;

        this.modalSlides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === nextIndex;

          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this.modalThumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === nextIndex;

          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.modalCurrent) {
          this.modalCurrent.textContent = String(nextIndex + 1);
        }

        if (this.modalTotal) {
          this.modalTotal.textContent = String(total);
        }

        const activeSlide = this.modalSlides[nextIndex];
        const isImage = activeSlide && activeSlide.dataset.mediaType === 'image';

        if (this.zoomButton) {
          this.zoomButton.hidden = !isImage;
          this.zoomButton.disabled = !isImage;
        }

        if (shouldPlay && this.isLightboxOpen) {
          this.syncModalVideos();
        }
      }

      toggleZoom() {
        const activeSlide = this.modalSlides[this.modalIndex];

        if (!activeSlide || activeSlide.dataset.mediaType !== 'image') return;

        const isZoomed = activeSlide.classList.toggle('is-zoomed');

        if (this.zoomButton) {
          this.zoomButton.classList.toggle('is-active', isZoomed);
          this.zoomButton.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
        }
      }

      resetZoom() {
        if (!this.modalSlides) return;

        this.modalSlides.forEach((slide) => slide.classList.remove('is-zoomed'));

        if (this.zoomButton) {
          this.zoomButton.classList.remove('is-active');
          this.zoomButton.setAttribute('aria-pressed', 'false');
        }
      }

      async shareProduct() {
        const shareData = {
          title: document.title,
          url: window.location.href,
        };

        try {
          if (navigator.share) {
            await navigator.share(shareData);
            return;
          }

          if (navigator.clipboard && this.shareButton) {
            await navigator.clipboard.writeText(shareData.url);
            this.shareButton.classList.add('is-success');

            window.setTimeout(() => {
              this.shareButton.classList.remove('is-success');
            }, 1200);
          }
        } catch (error) {
          // User cancelled share or clipboard was unavailable.
        }
      }

      toggleSlideshow() {
        if (this.slideShowTimer) {
          this.stopSlideshow();
          return;
        }

        if (this.playButton) {
          this.playButton.classList.add('is-active');
          this.playButton.setAttribute('aria-pressed', 'true');
          this.playButton.setAttribute('aria-label', 'Stop slideshow');
        }

        this.slideShowTimer = window.setInterval(() => {
          this.setModalIndex(this.modalIndex + 1);
        }, 1600);
      }

      stopSlideshow() {
        if (!this.slideShowTimer) return;

        window.clearInterval(this.slideShowTimer);
        this.slideShowTimer = null;

        if (this.playButton) {
          this.playButton.classList.remove('is-active');
          this.playButton.setAttribute('aria-pressed', 'false');
          this.playButton.setAttribute('aria-label', 'Start slideshow');
        }
      }

      async toggleFullscreen() {
        if (!this.lightboxDialog) return;

        try {
          if (!document.fullscreenElement) {
            await this.lightboxDialog.requestFullscreen();
          } else {
            await document.exitFullscreen();
          }
        } catch (error) {
          // Fullscreen API not available on this device/browser.
        }
      }

      onFullscreenChange() {
        if (!this.fullscreenButton) return;

        const isFullscreen = Boolean(document.fullscreenElement);

        this.fullscreenButton.classList.toggle('is-active', isFullscreen);
        this.fullscreenButton.setAttribute('aria-pressed', isFullscreen ? 'true' : 'false');
      }

      onResize() {
        if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);

        this.resizeRafId = requestAnimationFrame(() => {
          if (this.isLightboxOpen && window.innerWidth >= 750) {
            this.closeLightbox();
          }

          this.updateGalleryState(this.activeIndex, false);
        });
      }

      onScroll() {
        if (!this.isTabletOrMobile()) return;

        if (this.rafId) cancelAnimationFrame(this.rafId);

        this.rafId = requestAnimationFrame(() => {
          const closestIndex = this.getClosestIndex();

          if (closestIndex !== this.activeIndex) {
            this.activeIndex = closestIndex;
            this.updateGalleryState(this.activeIndex, true);
          }
        });
      }

      getClosestIndex() {
        if (!this.list || !this.items.length) return 0;

        const slideWidth = this.list.clientWidth || this.items[0]?.clientWidth || 1;
        const index = Math.round(this.list.scrollLeft / slideWidth);

        return Math.max(0, Math.min(index, this.items.length - 1));
      }

      goToIndex(index) {
        this.cache();

        const total = this.items.length;
        if (!total) return;

        const nextIndex = (index + total) % total;
        this.activeIndex = nextIndex;

        const target = this.items[nextIndex];

        if (target && this.isTabletOrMobile()) {
          this.list.scrollTo({
            left: target.offsetLeft,
            behavior: this.getScrollBehavior(),
          });
        }

        this.updateGalleryState(nextIndex, true);
      }

      updateGalleryState(index, shouldSyncVideos = true) {
        this.cache();

        this.items.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === index);
        });

        this.dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === index;

          dot.classList.toggle('is-active', isActive);

          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        if (shouldSyncVideos) {
          this.syncInlineVideos();
        }
      }

      pauseAllVideos(scope) {
        if (!scope) return;

        scope.querySelectorAll('video').forEach((video) => {
          try {
            video.pause();
          } catch (error) {
            // Ignore browser pause errors.
          }
        });
      }

      playMutedVideo(video) {
        if (!video) return;

        try {
          video.muted = true;
          video.playsInline = true;

          const promise = video.play();

          if (promise && typeof promise.catch === 'function') {
            promise.catch(() => {});
          }
        } catch (error) {
          // Ignore browser autoplay errors.
        }
      }

      syncInlineVideos() {
        if (this.isLightboxOpen) return;

        const shouldAvoidVideoAutoplay = !this.hasUserInteracted || this.saveDataEnabled() || this.prefersReducedMotion();

        this.items.forEach((item, index) => {
          const video = item.querySelector('video');

          if (!video) return;

          if (shouldAvoidVideoAutoplay) {
            video.pause();
            return;
          }

          if (index === this.activeIndex) {
            this.playMutedVideo(video);
          } else {
            video.pause();
          }
        });
      }

      syncModalVideos() {
        if (!this.modalSlides) return;

        this.modalSlides.forEach((slide, index) => {
          const video = slide.querySelector('video');

          if (!video) return;

          if (index === this.modalIndex) {
            this.playMutedVideo(video);
          } else {
            video.pause();
          }
        });
      }

      setActiveMedia(mediaId) {
        this.cache();

        const targetIndex = this.items.findIndex((item) => {
          return item.dataset.mediaId === mediaId || item.dataset.rawMediaId === String(mediaId);
        });

        if (targetIndex === -1) return;

        this.activeIndex = targetIndex;
        this.updateGalleryState(targetIndex, false);

        if (this.isTabletOrMobile()) {
          const target = this.items[targetIndex];

          if (target && this.list) {
            this.list.scrollTo({
              left: target.offsetLeft,
              behavior: 'auto',
            });
          }
        }
      }
    }
  );
}